// What Home reads beyond the scorecard (HOME-CAD-UX 2026-10-21): the org chart's seat holders
// (shared with Chart, Work and the Cadence sidebar: one cached call), the caller's unread inbox
// (the rail's badge shares it), the caller's open tasks (`my.tasks`) and the caller's own person
// row when `/v1/me` carries no name. Routes an API may not serve yet answer `null`, never an
// error, so a widget is absent instead of broken.

import { periodFromKey } from "@companyos/domain-kpi";
import { ApiError } from "@companyos/sdk";
import { queryOptions } from "@tanstack/react-query";
import type { Api } from "../../api/client";
import type { Band, MeResponse } from "../../api/types";
import { agentKeys } from "../agents/agent-queries";
import { orgChartQuery } from "../chart/chart-queries";
import type { CompanyAccess } from "../company";
import type { CompanyCalendar } from "../entry/calendar";
import { formatMinutes } from "../format";
import { coverageText } from "../screen-parts";
import { inboxQuery, myTasksQuery, pendingAsNull } from "../work/work-queries";

export { inboxQuery, myTasksQuery, orgChartQuery };

/**
 * The bounds of week `week` in the company's calendar (its time zone, week start and week
 * numbering, as the scorecard's weeks), as UTC instants; `[from, to)`.
 */
export function companyWeekBounds(
  cal: CompanyCalendar,
  week: string,
): { from: string; to: string } {
  const p = periodFromKey(cal, "week", week);
  return { from: p.startsAt.toISOString(), to: p.endsAt.toISOString() };
}

/**
 * HOME-DASH: the digital workforce's runs in one week of the company's calendar
 * (`workforce.runtime`): count, active time, tokens, cost. Under the runs key, so a run change
 * refreshes it.
 */
export const runtimeQuery = (api: Api, co: string, week: string, cal: CompanyCalendar) => {
  const bounds = companyWeekBounds(cal, week);
  return queryOptions({
    queryKey: [...agentKeys.runs(co), "runtime", week, bounds.from, bounds.to] as const,
    queryFn: () => api.client.workforce.runtime({ params: { co }, query: bounds }),
    refetchInterval: 60_000,
  });
};

/** "< 1 min", "12 min", "3 h 05 min" (summed run time). */
export function formatActive(ms: number): string {
  if (ms <= 0) return "0 min";
  if (ms < 60_000) return "< 1 min";
  return formatMinutes(ms / 60_000);
}

/** The caller's own person row (reader access): the display name when `/v1/me` has none. */
export const peopleMeQuery = (api: Api, co: string) =>
  queryOptions({
    queryKey: ["company", co, "people", "me"] as const,
    queryFn: () => pendingAsNull(() => api.client.people.me({ params: { co } })),
    staleTime: 5 * 60_000,
  });

/** The caller's membership in this company (`seat_assignment.membership_id` matches it). */
export function myMembershipId(me: MeResponse, company: CompanyAccess): string | null {
  return me.memberships.find((m) => m.company_id === company.id)?.membership_id ?? null;
}

/** A 403 on the org chart (05 `org_chart#viewer`: a viewer who holds no seat) is "no names". */
export function isForbidden(error: unknown): boolean {
  return error instanceof ApiError && error.status === 403;
}

export type Holder = {
  membership_id: string;
  kind: "person" | "digital_employee" | "personal_agent";
  name: string;
};

/** Seat id → its active holders (people first), from the org chart. */
export function holdersOf(
  chart:
    | {
        seats: {
          id: string;
          holders: { membership_id: string; membership_kind: string; display_name: string }[];
        }[];
      }
    | null
    | undefined,
): Map<string, Holder[]> {
  const out = new Map<string, Holder[]>();
  for (const s of chart?.seats ?? [])
    out.set(
      s.id,
      [...s.holders]
        .sort(
          (a, b) =>
            (a.membership_kind === "person" ? -1 : 1) - (b.membership_kind === "person" ? -1 : 1),
        )
        .map((h) => ({
          membership_id: h.membership_id,
          kind: h.membership_kind as Holder["kind"],
          name: h.display_name,
        })),
    );
  return out;
}

/** The hour's greeting (Work OS `greeting()`). */
/**
 * The name Home greets: the first name, unless the account is named after the company ("HTVA
 * founder", a seed's login), then the email's local part — the person, never the company.
 */
export function greetingName(fullName: string, companyName: string, email: string): string {
  const first = fullName.trim().split(/\s+/)[0] ?? "";
  const co = companyName.trim().toLowerCase();
  const named = first && first.toLowerCase() !== co && fullName.trim().toLowerCase() !== co;
  if (named) return first;
  const local = email.split("@")[0] ?? "";
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : "";
}

export function greeting(now: Date): string {
  const h = now.getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

/**
 * The KPI score tile: a number with a band when the data is in; a number with "provisional"
 * and no band pill while it is still coming in (a score and "awaiting" together contradict);
 * the "Awaiting data" pill only when there is no number at all.
 */
export function kpiTile(card: { kpi_score: number | null; completeness: number; band: Band }): {
  band?: Band;
  detail: string;
} {
  const pct = `${Math.round(card.completeness * 100)}%`;
  if (card.kpi_score === null) return { band: card.band, detail: `${pct} of the data in` };
  if (card.band === "insufficient_data") return { detail: `Provisional · ${pct} of the data in` };
  return { band: card.band, detail: coverageText(card.completeness, card.band) };
}

/** "3 min ago", "2 h ago", "4 d ago" (Work OS review rows). */
export function timeAgo(iso: string, now: Date = new Date()): string {
  const s = Math.max(0, Math.round((now.getTime() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return `${Math.round(h / 24)} d ago`;
}
